import React, { useEffect, useReducer, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Card, GameState, GameAction, Color } from '../types/game';
import { generateDeck, dealCards, isValidSet, findAllSets } from '../utils/gameUtils';
import DailyChallengeBoard from './DailyChallengeBoard';

const initialState: GameState = {
    deck: [],
    cardsInPlay: [],
    selectedCards: [],
    matchedCards: [],
    newlyDealtCards: [],
    isShuffling: false,
    score: 0,
    gameStarted: false,
    gameOver: false,
    timeElapsed: 0
};

function gameReducer(state: GameState, action: GameAction): GameState {
    switch (action.type) {
        case 'START_GAME': {
            const deck = generateDeck();
            const [cardsInPlay, remainingDeck] = dealCards(deck);
            
            return {
                ...state,
                deck: remainingDeck,
                cardsInPlay,
                selectedCards: [],
                matchedCards: [],
                newlyDealtCards: cardsInPlay,
                gameStarted: true,
                score: 0,
                timeElapsed: 0
            };
        }
        case 'SELECT_CARD': {
            if (state.matchedCards.length > 0) return state;
            
            const isSelected = state.selectedCards.some(card => card.id === action.card.id);
            let selectedCards = isSelected
                ? state.selectedCards.filter(card => card.id !== action.card.id)
                : [...state.selectedCards, action.card];
            
            if (selectedCards.length > 3) {
                selectedCards = [action.card];
            }
            
            return {
                ...state,
                selectedCards
            };
        }
        case 'CHECK_SET': {
            if (state.selectedCards.length !== 3) return state;

            const isSet = isValidSet(state.selectedCards);
            if (!isSet) {
                return {
                    ...state,
                    selectedCards: [],
                    score: Math.max(0, state.score - 1)
                };
            }

            return {
                ...state,
                matchedCards: state.selectedCards,
                score: state.score + 1
            };
        }
        case 'CLEAR_MATCHED_CARDS': {
            if (state.matchedCards.length === 0) return state;

            const remainingCards = state.cardsInPlay.filter(
                card => !state.matchedCards.some(matched => matched.id === card.id)
            );
            
            const [newCards, remainingDeck] = dealCards(
                state.deck, 
                state.matchedCards.length,
                remainingCards
            );

            const newCardsInPlay = [...remainingCards];

            // Fill the positions of the removed cards with new cards
            state.matchedCards.forEach((matchedCard, index) => {
                const position = state.cardsInPlay.findIndex(card => card.id === matchedCard.id);
                if (position !== -1 && newCards[index]) {
                    newCardsInPlay.splice(position, 0, newCards[index]);
                }
            });

            // Game is over when either:
            // 1. No cards left in deck AND no sets possible with current cards
            // 2. No cards left in play
            const gameOver = (remainingDeck.length === 0 && findAllSets(newCardsInPlay).length === 0) || 
                            newCardsInPlay.length === 0;

            return {
                ...state,
                deck: remainingDeck,
                cardsInPlay: newCardsInPlay,
                selectedCards: [],
                matchedCards: [],
                newlyDealtCards: newCards,
                gameOver
            };
        }
        case 'DEAL_MORE_CARDS': {
            const [newCards, remainingDeck] = dealCards(
                state.deck, 
                3,
                state.cardsInPlay
            );
            return {
                ...state,
                deck: remainingDeck,
                cardsInPlay: [...state.cardsInPlay, ...newCards],
                newlyDealtCards: newCards
            };
        }
        case 'UPDATE_TIME': {
            return {
                ...state,
                timeElapsed: action.time
            };
        }
        case 'CLEAR_DEALT_ANIMATION': {
            return {
                ...state,
                newlyDealtCards: []
            };
        }
        case 'SHUFFLE_CARDS': {
            const shuffledCards = [...state.cardsInPlay]
                .map((card, index) => ({ ...card, position: index }))
                .sort(() => Math.random() - 0.5);
            
            return {
                ...state,
                cardsInPlay: shuffledCards,
                isShuffling: true
            };
        }
        case 'END_SHUFFLE': {
            return {
                ...state,
                isShuffling: false
            };
        }
        case 'RESET_GAME': {
            return initialState;
        }
        case 'RESET_SELECTION': {
            return {
                ...state,
                selectedCards: []
            };
        }
        default:
            return state;
    }
}

type ColorMap = {
    [K in Color]: string;
};

export const COLORS: ColorMap = {
    crimson: '#DC143C',
    royalblue: '#4169E1',
    forestgreen: '#228B22'
};

const GameContainer = styled.div`
    width: 100%;
    min-height: 100vh;
    background-color: #f5f5f5;
    display: flex;
    flex-direction: column;
    align-items: center;

    .button-group {
        display: flex;
        gap: 15px;
        align-items: center;
    }

    .score-time {
        display: flex;
        gap: 20px;
        align-items: center;
        font-size: 20px;
    }
`;

const ContentWrapper = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px 80px 20px;

    @media (max-width: 768px) {
        padding: 0 10px 100px 10px;
    }
`;

const Header = styled.div`
    text-align: center;
    padding: 30px 20px;
    background-color: #4CAF50;
    margin-bottom: 20px;
    color: white;
    width: 100%;

    h1 {
        font-size: 48px;
        margin: 0 0 15px 0;
    }

    p {
        font-size: 24px;
        margin: 0;
        opacity: 0.9;
    }

    @media (max-width: 768px) {
        padding: 20px 10px;

        h1 {
            font-size: 36px;
            margin: 0 0 10px 0;
        }

        p {
            font-size: 18px;
        }
    }
`;

const Controls = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin: 30px 0;
    gap: 20px;

    .score-time {
        display: flex;
        gap: 20px;
        align-items: center;
        font-size: 20px;
        font-family: 'Courier New', monospace;

        .timer {
            min-width: 80px;
            text-align: left;
        }

        .score {
            min-width: 160px;
            text-align: left;
        }

        .deck-count {
            min-width: 120px;
            text-align: left;
        }
    }

    @media (min-width: 769px) {
        flex-direction: row;
        justify-content: space-between;

        .button-group {
            display: flex;
            gap: 15px;
        }
    }

    @media (max-width: 768px) {
        margin: 20px 0;
        gap: 15px;

        .button-group {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            justify-content: center;
        }

        .score-time {
            display: flex;
            gap: 15px;
            justify-content: center;
            font-size: 16px;
            flex-wrap: wrap;

            .timer {
                min-width: 70px;
            }

            .score {
                min-width: 140px;
            }

            .deck-count {
                min-width: 110px;
            }
        }
    }
`;

const Board = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: repeat(3, 1fr);
    gap: 10px;
    margin: 20px auto 40px auto;
    max-width: 1000px;
    padding: 0 10px;

    @media (max-width: 768px) {
        grid-template-columns: repeat(3, 1fr);
        grid-template-rows: repeat(4, 1fr);
        gap: 8px;
        margin: 15px auto 30px auto;
        padding: 0;
    }
`;

const Button = styled.button`
    padding: 10px 20px;
    font-size: 16px;
    border-radius: 5px;
    border: none;
    background-color: #4CAF50;
    color: white;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        background-color: #45a049;
    }
    &:disabled {
        background-color: #cccccc;
        cursor: not-allowed;
    }

    @media (max-width: 768px) {
        padding: 8px 16px;
        font-size: 14px;
    }
`;

const DebugSection = styled.div`
    margin-top: 20px;
    padding: 10px;
    background-color: #f8f9fa;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-family: monospace;
    white-space: pre-wrap;
    width: 100%;
    font-size: 14px;

    @media (max-width: 768px) {
        font-size: 12px;
        margin-top: 15px;
        padding: 8px;
    }
`;

const pulseAnimation = keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
`;

const flipAnimation = keyframes`
    0% { transform: rotateY(0deg); }
    100% { transform: rotateY(360deg); }
`;

const dealAnimation = keyframes`
    0% {
        transform: translateY(-100vh) rotate(-10deg);
        opacity: 0;
    }
    100% {
        transform: translateY(0) rotate(0);
        opacity: 1;
    }
`;

const shuffleAnimation = keyframes`
    0% {
        transform: translate(0, 0) rotate(0);
    }
    25% {
        transform: translate(var(--shuffle-x), var(--shuffle-y)) rotate(180deg);
    }
    50% {
        transform: translate(0, 0) rotate(360deg);
    }
    75% {
        transform: translate(calc(var(--shuffle-x) * -0.5), calc(var(--shuffle-y) * -0.5)) rotate(180deg);
    }
    100% {
        transform: translate(0, 0) rotate(0);
    }
`;

const shakeAnimation = keyframes`
    0% { transform: translateX(0); }
    25% { transform: translateX(-5px); }
    50% { transform: translateX(5px); }
    75% { transform: translateX(-5px); }
    100% { transform: translateX(0); }
`;

const CardContainer = styled.div<{ 
    selected: boolean; 
    isMatched: boolean; 
    isNew: boolean;
    isShuffling: boolean;
    shuffleX?: number;
    shuffleY?: number;
    shake: boolean;
}>`
    aspect-ratio: 1;
    border: 3px solid ${props => props.selected ? '#4CAF50' : '#ddd'};
    border-radius: 10px;
    padding: 10px;
    cursor: pointer;
    background-color: white;
    box-shadow: ${props => props.selected ? '0 0 10px rgba(76, 175, 80, 0.5)' : '0 2px 5px rgba(0,0,0,0.1)'};
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: relative;
    transform-style: preserve-3d;

    ${props => props.isShuffling && css`
        --shuffle-x: ${props.shuffleX}px;
        --shuffle-y: ${props.shuffleY}px;
        animation: ${shuffleAnimation} 1s ease-in-out;
    `}

    ${props => props.selected && !props.isShuffling && css`
        animation: ${pulseAnimation} 1s infinite;
    `}
    ${props => props.isMatched && !props.isShuffling && css`
        animation: ${flipAnimation} 1s;
    `}
    ${props => props.isNew && !props.isShuffling && css`
        animation: ${dealAnimation} 0.5s ease-out;
    `}
    ${props => props.shake && css`
        animation: ${shakeAnimation} 0.3s;
    `}

    &:hover {
        transform: ${props => !props.isMatched && !props.isShuffling && 'translateY(-5px)'};
        box-shadow: ${props => !props.isMatched && !props.isShuffling && '0 5px 15px rgba(0,0,0,0.2)'};
    }

    @media (max-width: 768px) {
        padding: 8px;
        border-width: 2px;
    }

    @media (max-width: 480px) {
        padding: 5px;
        border-width: 2px;
    }
`;

const Shape = styled.div<{ color: string; shading: string }>`
    position: absolute;
    width: 30%;
    height: 30%;
    display: flex;
    justify-content: center;
    align-items: center;

    svg {
        height: 100%;
        width: 100%;
        fill: ${props => {
            switch (props.shading) {
                case 'solid':
                    return props.color;
                case 'dotted':
                    return `url(#dotted-${Object.entries(COLORS).find(([_, value]) => value === props.color)?.[0]})`;
                case 'crosshatched':
                    return `url(#crosshatch-${Object.entries(COLORS).find(([_, value]) => value === props.color)?.[0]})`;
                default:
                    return 'none';
            }
        }};
        stroke: ${props => props.color};
        stroke-width: 2;
    }

    @media (max-width: 480px) {
        width: 35%;
        height: 35%;
        
        svg {
            stroke-width: 1.5;
        }
    }
`;

const TutorialSection = styled.div`
    margin: 30px 0;
    padding: 40px;
    background-color: #ffffff;
    border: 2px solid #e0e0e0;
    border-radius: 10px;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    line-height: 1.8;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const MiniCardContainer = styled.div<{ color: string; shading: string }>`
    width: 60px;
    height: 80px;
    border: 2px solid #ddd;
    border-radius: 5px;
    background-color: white;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 5px;
    position: relative;
    box-shadow: 0 2px 5px rgba(0,0,0,0.1);
`;

const MiniShape = styled.div<{ color: string; shading: string }>`
    width: 20px;
    height: 20px;
    display: flex;
    justify-content: center;
    align-items: center;

    svg {
        height: 100%;
        width: 100%;
        fill: ${props => {
            switch (props.shading) {
                case 'solid':
                    return props.color;
                case 'dotted':
                    return `url(#dotted-${Object.entries(COLORS).find(([_, value]) => value === props.color)?.[0]})`;
                case 'crosshatched':
                    return `url(#crosshatch-${Object.entries(COLORS).find(([_, value]) => value === props.color)?.[0]})`;
                default:
                    return 'none';
            }
        }};
        stroke: ${props => props.color};
        stroke-width: 1;
    }
`;

const MiniCard: React.FC<{ card: Card }> = ({ card }) => (
    <MiniCardContainer color={COLORS[card.color]} shading={card.shading}>
        {Array(card.number).fill(0).map((_, i) => (
            <MiniShape key={i} color={COLORS[card.color]} shading={card.shading}>
                <svg viewBox="0 0 30 30">
                    <path d={getShapePath(card.shape)} />
                </svg>
            </MiniShape>
        ))}
    </MiniCardContainer>
);

const Tutorial: React.FC = () => (
    <TutorialSection>
        <Header>
            <h1>Set Card Game</h1>
            <p>Find matching sets of three cards!</p>
        </Header>
        <h2 style={{ color: '#4CAF50', textAlign: 'center', marginBottom: '30px', fontSize: '28px' }}>How to Play Set</h2>
        <p style={{ marginBottom: '20px' }}>A set consists of three cards where each feature (color, shape, number, and shading) must be either all the same OR all different across the cards. No feature can be the same on just two cards.</p>
        <h3 style={{ color: '#4169E1', marginBottom: '15px', fontSize: '22px' }}>Card Features</h3>
        <ul style={{ listStyleType: 'none', paddingLeft: '0', marginBottom: '20px' }}>
            <li><strong>Color:</strong> Red, Blue, Green</li>
            <li><strong>Shape:</strong> Triangle, Circle, Rectangle</li>
            <li><strong>Number:</strong> 1, 2, or 3 shapes</li>
            <li><strong>Shading:</strong> Solid, Dotted, Crosshatched</li>
        </ul>
        <h3 style={{ color: '#DC143C', marginBottom: '15px', fontSize: '22px' }}>Example Set</h3>
        <p style={{ marginBottom: '20px' }}>In this example, we have a set of three cards where each feature is either ALL the same, or ALL different..</p>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '30px' }}>
            <MiniCard card={{ id: '1', color: 'crimson', shape: 'triangle', number: 1, shading: 'solid' }} />
            <MiniCard card={{ id: '2', color: 'royalblue', shape: 'triangle', number: 2, shading: 'dotted' }} />
            <MiniCard card={{ id: '3', color: 'forestgreen', shape: 'triangle', number: 3, shading: 'crosshatched' }} />
        </div>
        <ul style={{ listStyleType: 'none', paddingLeft: '0', marginBottom: '30px' }}>
            <li>✅ Shape: All the same (triangles)</li>
            <li>❌ Color: All different</li>
            <li>❌ Number: All different (1, 2, 3)</li>
            <li>❌ Shading: All different</li>
        </ul>
        <h3 style={{ color: '#228B22', marginBottom: '15px', fontSize: '22px' }}>Goal</h3>
        <p>Find as many sets as you can! Select three cards that form a set. Correct sets are removed and replaced with new cards.</p>
    </TutorialSection>
);

const Game: React.FC = () => {
    const [state, dispatch] = useReducer(gameReducer, initialState);
    const [dailyChallengeCards, setDailyChallengeCards] = React.useState<Card[]>([]);
    const [isDailyChallenge, setIsDailyChallenge] = React.useState(false);
    const [showTutorial, setShowTutorial] = React.useState(true);
    const [completedSets, setCompletedSets] = React.useState<Card[][]>([]);
    const [selectedCards, setSelectedCards] = React.useState<Card[]>([]);
    const [shakingCards, setShakingCards] = React.useState<string[]>([]);

    useEffect(() => {
        if (state.gameStarted && !state.gameOver) {
            const timer = setInterval(() => {
                dispatch({ type: 'UPDATE_TIME', time: state.timeElapsed + 1 });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [state.gameStarted, state.gameOver, state.timeElapsed]);

    const handleCardClick = useCallback((card: Card) => {
        dispatch({ type: 'SELECT_CARD', card });
    }, []);

    useEffect(() => {
        if (state.selectedCards.length === 3) {
            const timer = setTimeout(() => {
                const isSet = isValidSet(state.selectedCards);
                if (isSet) {
                    dispatch({ type: 'CHECK_SET' });
                } else {
                    setShakingCards(state.selectedCards.map(card => card.id));
                    setTimeout(() => setShakingCards([]), 500); // Reset shake after animation
                    dispatch({ type: 'RESET_SELECTION' });
                }
            }, 200); // Increase delay to ensure green border appears

            return () => clearTimeout(timer);
        }
    }, [state.selectedCards]);

    useEffect(() => {
        if (state.matchedCards.length > 0) {
            const timer = setTimeout(() => {
                dispatch({ type: 'CLEAR_MATCHED_CARDS' });
            }, 1000); // Wait for flip animation to complete
            return () => clearTimeout(timer);
        }
    }, [state.matchedCards]);

    useEffect(() => {
        if (state.newlyDealtCards.length > 0) {
            const timer = setTimeout(() => {
                dispatch({ type: 'CLEAR_DEALT_ANIMATION' });
            }, 500); // Match the animation duration
            return () => clearTimeout(timer);
        }
    }, [state.newlyDealtCards]);

    useEffect(() => {
        if (state.isShuffling) {
            const timer = setTimeout(() => {
                dispatch({ type: 'END_SHUFFLE' });
            }, 1000); // Match shuffle animation duration
            return () => clearTimeout(timer);
        }
    }, [state.isShuffling]);

    useEffect(() => {
        console.log("Cards in play:", state.cardsInPlay);
    }, [state.cardsInPlay]);

    const generateDailyChallengeBoard = () => {
        let deck = generateDeck();
        let tempCardsInPlay: Card[] = [];
        let attempts = 0;
        
        while (findAllSets(tempCardsInPlay).length !== 6 && attempts < 1000) {
            deck = generateDeck();
            [tempCardsInPlay] = dealCards(deck, 12); // Deal 12 cards
            attempts++;
        }

        if (findAllSets(tempCardsInPlay).length === 6) {
            console.log('Daily Challenge Board:', tempCardsInPlay);
            return tempCardsInPlay;
        } else {
            console.error('Failed to generate a board with exactly 6 sets.');
            return [];
        }
    };

    const handleDailyChallenge = () => {
        const cards = generateDailyChallengeBoard();
        setDailyChallengeCards(cards);
        setIsDailyChallenge(true);
        dispatch({ type: 'START_GAME' }); // Start the timer
        setShowTutorial(false); // Hide the tutorial
    };

    return (
        <GameContainer>
            <ContentWrapper>
                <Controls>
                    <div className="button-group">
                        <Button onClick={() => {
                            dispatch({ type: 'START_GAME' });
                            setShowTutorial(false);
                        }}>
                            {state.gameStarted ? 'Restart Game' : 'Start Game'}
                        </Button>
                        {!isDailyChallenge && (
                            <Button onClick={handleDailyChallenge}>
                                Daily Challenge
                            </Button>
                        )}
                        {state.gameStarted && !isDailyChallenge && (
                            <Button
                                onClick={() => dispatch({ type: 'SHUFFLE_CARDS' })}
                                disabled={state.isShuffling || state.matchedCards.length > 0}
                            >
                                Shuffle Cards
                            </Button>
                        )}
                        <Button onClick={() => {
                            setIsDailyChallenge(false);
                            setDailyChallengeCards([]);
                            dispatch({ type: 'RESET_GAME' });
                            setShowTutorial(true);
                        }}>
                            Home
                        </Button>
                    </div>
                    <div className="score-time">
                        <strong className="score">Sets Found: {state.score}</strong>
                        <strong className="deck-count">Cards Left: {state.deck.length}</strong>
                        <strong className="timer">Time: {Math.floor(state.timeElapsed / 60)}:{(state.timeElapsed % 60).toString().padStart(2, '0')}</strong>
                    </div>
                </Controls>

                {showTutorial && <Tutorial />}

                {state.gameOver && (
                    <div style={{ textAlign: 'center', margin: '20px 0' }}>
                        <h2>Game Over!</h2>
                        <p>Sets Completed: {state.score}</p>
                    </div>
                )}

                {isDailyChallenge ? (
                    <>
                        <DailyChallengeBoard cards={dailyChallengeCards} />
                        <DebugSection>
                            <h3>Debug Information</h3>
                            <p>Number of possible sets: {findAllSets(dailyChallengeCards).length}</p>
                            <div>
                                {findAllSets(dailyChallengeCards).map((set, index) => (
                                    <div key={index} style={{ marginTop: '10px' }}>
                                        <strong>Set {index + 1}:</strong>
                                        {set.map(card => (
                                            <div key={card.id} style={{ marginLeft: '20px' }}>
                                                {card.number} {card.color} {card.shape} {card.shading}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </DebugSection>
                    </>
                ) : (
                    <>
                        <Board>
                            {state.cardsInPlay.map((card, currentIndex) => {
                                const originalIndex = card.position ?? currentIndex;
                                const shuffleX = (currentIndex % 4 - originalIndex % 4) * 250;
                                const shuffleY = (Math.floor(currentIndex / 4) - Math.floor(originalIndex / 4)) * 250;
                                
                                return (
                                    <CardComponent
                                        key={card.id}
                                        card={card}
                                        selected={state.selectedCards.some(selected => selected.id === card.id)}
                                        isMatched={state.matchedCards.some(matched => matched.id === card.id)}
                                        isNew={state.newlyDealtCards.some(newCard => newCard.id === card.id)}
                                        isShuffling={state.isShuffling}
                                        shuffleX={shuffleX}
                                        shuffleY={shuffleY}
                                        onClick={() => handleCardClick(card)}
                                        shake={shakingCards.includes(card.id)}
                                    />
                                );
                            })}
                        </Board>
                        <DebugSection>
                            <h3>Debug Information</h3>
                            <p>Number of possible sets: {findAllSets(state.cardsInPlay).length}</p>
                            <div>
                                {findAllSets(state.cardsInPlay).map((set, index) => (
                                    <div key={index} style={{ marginTop: '10px' }}>
                                        <strong>Set {index + 1}:</strong>
                                        {set.map(card => (
                                            <div key={card.id} style={{ marginLeft: '20px' }}>
                                                {card.number} {card.color} {card.shape} {card.shading}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </DebugSection>
                    </>
                )}
            </ContentWrapper>
            <svg style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
                <defs>
                    {(Object.entries(COLORS) as [Color, string][]).map(([colorName, colorValue]) => (
                        <React.Fragment key={colorName}>
                            <pattern
                                id={`dotted-${colorName}`}
                                patternUnits="userSpaceOnUse"
                                width="6"
                                height="6"
                            >
                                <circle
                                    cx="3"
                                    cy="3"
                                    r="1.5"
                                    fill={colorValue}
                                />
                            </pattern>
                            <pattern
                                id={`crosshatch-${colorName}`}
                                patternUnits="userSpaceOnUse"
                                width="4"
                                height="4"
                            >
                                <path
                                    d="M-1,1 l2,-2 M0,4 l4,-4 M3,5 l2,-2"
                                    stroke={colorValue}
                                    strokeWidth="1"
                                />
                            </pattern>
                        </React.Fragment>
                    ))}
                </defs>
            </svg>
        </GameContainer>
    );
};

interface CardComponentProps {
    card: Card;
    selected: boolean;
    isMatched: boolean;
    isNew: boolean;
    isShuffling: boolean;
    shuffleX?: number;
    shuffleY?: number;
    onClick: () => void;
    shake: boolean;
}

const getShapePath = (shape: string) => {
    switch (shape) {
        case 'triangle':
            return 'M15,2 L28,28 L2,28 Z';
        case 'circle':
            return 'M15,15 m-13,0 a13,13 0 1,0 26,0 a13,13 0 1,0 -26,0';
        case 'rectangle':
            return 'M3,3 H27 V27 H3 Z';
        default:
            return '';
    }
};

export const CardComponent: React.FC<CardComponentProps> = ({ 
    card, 
    selected, 
    isMatched, 
    isNew,
    isShuffling,
    shuffleX,
    shuffleY,
    onClick,
    shake
}) => {
    return (
        <CardContainer 
            selected={selected} 
            isMatched={isMatched} 
            isNew={isNew}
            isShuffling={isShuffling}
            shuffleX={shuffleX}
            shuffleY={shuffleY}
            onClick={onClick}
            shake={shake}
        >
            <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                position: 'relative'
            }}>
                {Array(Number(card.number)).fill(0).map((_, i) => (
                    <Shape 
                        key={i} 
                        color={COLORS[card.color]} 
                        shading={card.shading}
                        style={{
                            position: 'absolute',
                            ...(card.number === 1 && {
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)'
                            }),
                            ...(card.number === 2 && {
                                ...(i === 0 && {
                                    top: '30%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)'
                                }),
                                ...(i === 1 && {
                                    top: '70%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)'
                                })
                            }),
                            ...(card.number === 3 && {
                                ...(i === 0 && {
                                    top: '25%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)'
                                }),
                                ...(i === 1 && {
                                    top: '75%',
                                    left: '25%',
                                    transform: 'translate(-50%, -50%)'
                                }),
                                ...(i === 2 && {
                                    top: '75%',
                                    left: '75%',
                                    transform: 'translate(-50%, -50%)'
                                })
                            })
                        }}
                    >
                        <svg 
                            viewBox="0 0 30 30" 
                            style={{
                                width: '100%',
                                height: '100%'
                            }}
                        >
                            <path d={getShapePath(card.shape)} />
                        </svg>
                    </Shape>
                ))}
            </div>
        </CardContainer>
    );
};

// Add this temporarily to test the layout
const testCard = {
    id: 'test',
    color: 'red',
    shape: 'circle',
    number: 3,
    shading: 'solid'
};

console.log("Current cards:", /* your cards array here */);

export default Game; 